/**
 * The route's cross-section as **geometry**, not as a picture.
 *
 * One continuous profile for the whole route — not twelve sparklines. That is
 * the difference the pane exists for: twelve boxes with twelve private vertical
 * scales cannot be compared with each other, and the number a pilot actually
 * wants (how far the planned altitude sits above the highest thing under it) is
 * a comparison ACROSS legs. Here every leg is drawn against one scale, the
 * planned altitude is on the picture, and the gap between the two is the shape
 * the eye reads first.
 *
 * Nothing in this file touches React, the DOM or the network, and no component
 * is allowed to compute a coordinate: the rules below are pinned down by
 * `test/terrain-profile.test.ts` instead of by looking at the chart and hoping.
 * It is the same discipline `terrain.ts`'s `sparkline()` already follows, one
 * level up.
 *
 * Four decisions worth writing down.
 *
 * **The terrain line is an ENVELOPE, not a sample line.** A 12-leg route carries
 * a few thousand samples and the drawing is ~1 000 units wide, so several
 * samples share a column. The one that is drawn is the HIGHEST of them: a peak
 * that survives thinning is a peak the pilot sees, and a peak that is averaged
 * away is a peak the pilot does not. Averaging a terrain profile is how a chart
 * lies about the one thing it is for.
 *
 * **A sample with no elevation BREAKS the line** (same rule as the sparkline):
 * `elev_ft: null` means the archive has no tile there, which is neither sea
 * level nor zero. A real `0 ft` over the Adriatic is a measurement and is drawn
 * as one. A column that simply caught no sample (bucketing granularity, not
 * missing data) does not break anything.
 *
 * **The vertical scale is driven by the plan, not by the ground.** The top is
 * 15 % above the highest of {terrain, obstacle, planned altitude, suggested
 * Sicherheitshöhe}, so the planned altitude is always ON the picture — a chart
 * scaled to the terrain alone puts a 9 500 ft cruise off the top of the box and
 * the clearance becomes invisible exactly where it is most comfortable.
 *
 * **Clearance is per leg and it is a number before it is a colour.** `ok` /
 * `low` / `none` is decided here; the component only picks the token.
 */

import type { TerrainLeg, TerrainObstacle, TerrainSample } from '../../api';

/* ================================================================== *
 * the box
 * ================================================================== */

/** The drawing's own units. CSS stretches the box; `vector-effect` keeps strokes 1 px. */
export const PROFILE_W = 1000;
export const PROFILE_H = 280;
/** Half a stroke of air over the highest thing drawn, so nothing is clipped. */
const PAD_TOP = 6;
/** How much room over the highest hazard/altitude the scale leaves. */
const HEADROOM = 1.15;
/** A route with nothing above sea level still needs a box to draw in. */
const MIN_TOP_FT = 1000;
/** At most one drawn point per two units of width — see the envelope note above. */
const MAX_COLUMNS = 500;

/** Below this many feet over the highest hazard the clearance is a caution. */
export const LOW_CLEARANCE_FT = 1000;

/* ================================================================== *
 * clearance
 * ================================================================== */

/** `ok` · `low` (under the suggestion) · `none` (at or under the hazard). */
export type ClearanceLevel = 'ok' | 'low' | 'none';

/**
 * How the planned altitude stands to the ground under it.
 *
 * `none` is the only one that is a fact about terrain: the planned altitude is
 * at or below the highest thing on the leg. `low` follows the SUGGESTION where
 * there is one — the endpoint applies +1 000 ft over flat ground and +2 000 ft
 * over mountains, and a rule the pilot is shown must be the rule they are
 * measured against — and falls back to a flat 1 000 ft where it has none.
 *
 * With no planned altitude there is nothing to compare and the answer is `ok`:
 * an empty Höhe/FL cell is an unfinished plan, not a dangerous one, and painting
 * every unfinished plan red teaches pilots to ignore red.
 */
export function clearanceLevel(
  plannedFt: number | null,
  hazardFt: number | null,
  suggestedFt: number | null,
): ClearanceLevel {
  if (plannedFt == null) return 'ok';
  if (hazardFt != null && plannedFt <= hazardFt) return 'none';
  if (suggestedFt != null) return plannedFt < suggestedFt ? 'low' : 'ok';
  if (hazardFt != null && plannedFt - hazardFt < LOW_CLEARANCE_FT) return 'low';
  return 'ok';
}

/** Planned minus the highest thing under it, or null when either is missing. */
export const clearanceFt = (plannedFt: number | null, hazardFt: number | null): number | null =>
  plannedFt == null || hazardFt == null ? null : plannedFt - hazardFt;

/* ================================================================== *
 * the model
 * ================================================================== */

export interface ProfileObstacle {
  /**
   * Unique within the drawing: `leg-id`. The openAIP id alone is NOT unique —
   * an obstacle inside the corridor of two consecutive legs is returned once per
   * leg, and React drew the two ticks as one.
   */
  key: string;
  /** the openAIP id, for the map */
  id: string;
  /** the FORM's leg number, 1-based */
  leg: number;
  /** cumulative distance from the departure, NM */
  nm: number;
  x: number;
  y: number;
  ft: number;
  /** false → `ft` is the GROUND under it and the top is higher by an unknown amount */
  exact: boolean;
  name: string;
  /** openAIP's type slug — the RENDERER translates it */
  kind: string;
  /** it reaches the planned altitude of its own leg */
  conflict: boolean;
}

export interface ProfileLeg {
  /** the FORM's leg number, 1-based */
  leg: number;
  startNm: number;
  endNm: number;
  distNm: number;
  x1: number;
  x2: number;
  /** the highest thing on the leg: ground or obstacle */
  hazardFt: number | null;
  maxTerrainFt: number | null;
  /** where that ground is, cumulative NM */
  maxTerrainAtNm: number | null;
  /** the tallest obstacle of the leg, or null */
  obstacle: { name: string; kind: string; ft: number; exact: boolean } | null;
  plannedFt: number | null;
  suggestedFt: number | null;
  clearanceFt: number | null;
  level: ClearanceLevel;
  /** the shaded gap between the planned altitude and the hazard; null when there is none */
  band: { x: number; w: number; y: number; h: number } | null;
}

export interface ProfileModel {
  w: number;
  h: number;
  totalNm: number;
  /** the top of the scale, ft */
  topFt: number;
  /** the terrain envelope, one polyline per unbroken run */
  lines: string[];
  /** the same runs closed to the baseline, for the fill */
  areas: string[];
  obstacles: ProfileObstacle[];
  /** the planned altitude as steps, one polyline per unbroken run of legs */
  planned: string[];
  /** the suggested Sicherheitshöhe as steps, same shape */
  suggested: string[];
  legs: ProfileLeg[];
  /** every waypoint on the x axis: where it is and what it is called */
  marks: { index: number; nm: number; x: number }[];
  /** horizontal gridlines, in feet */
  grid: { ft: number; y: number }[];
  /** the highest thing anywhere on the route */
  highestFt: number | null;
  /** the SMALLEST clearance on the route — the number that says whether it flies */
  minClearanceFt: number | null;
  /** the worst level any leg reaches */
  level: ClearanceLevel;
  /** the first place the planned altitude does not clear the ground under it */
  firstConflict: { leg: number; nm: number; x: number; y: number } | null;
  /** true when there is nothing to draw at all */
  empty: boolean;
}

export interface ProfileInput {
  /** the endpoint's legs, 0-based, in route order */
  legs: readonly TerrainLeg[];
  /** the planned altitude of the FORM's leg n, ft MSL — `null` when the cell is empty */
  planned(leg: number): number | null;
  /** the suggested Sicherheitshöhe of the FORM's leg n, ft MSL */
  suggested(leg: number): number | null;
  size?: { w?: number; h?: number } | undefined;
}

const round1 = (n: number): number => Math.round(n * 10) / 10;
const round2 = (n: number): number => Math.round(n * 100) / 100;

/** The tallest obstacle of a leg, ignoring the ones published without a height. */
function tallest(list: readonly TerrainObstacle[]): TerrainObstacle | null {
  let best: TerrainObstacle | null = null;
  for (const o of list) {
    if (o.elev_ft == null) continue;
    if (!best || o.elev_ft > (best.elev_ft ?? -Infinity)) best = o;
  }
  return best;
}

/**
 * The top of the vertical scale: 15 % over the highest thing that has to be on
 * the picture, rounded up to something a gridline can carry.
 */
export function scaleTopFt(highestFt: number): number {
  const raw = Math.max(MIN_TOP_FT, highestFt * HEADROOM);
  const step = raw <= 5000 ? 500 : raw <= 20000 ? 1000 : 2000;
  return Math.ceil(raw / step) * step;
}

/**
 * Three to five horizontal lines, on a round number of feet.
 *
 * The step is the SMALLEST round number that keeps the count at five or fewer —
 * `topFt / 6` rounded up to the next nice value — which is what gives a
 * 1 500 ft box the same visual density as a 30 000 ft one. Picking the step from
 * `topFt / 4` instead leaves a tall box with two lines and nothing to read
 * against.
 */
export function gridSteps(topFt: number): number[] {
  const nice = [100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000];
  const step = nice.find(s => s >= topFt / 6) ?? 10000;
  const out: number[] = [];
  for (let ft = step; ft < topFt; ft += step) out.push(ft);
  return out;
}

/**
 * The whole route as one drawing.
 *
 * `legs` are the endpoint's 0-based legs; every leg number this model publishes
 * is the FORM's 1-based one, because that is the number printed beside it
 * everywhere else in the app.
 */
export function profileModel(input: ProfileInput): ProfileModel {
  const w = input.size?.w ?? PROFILE_W;
  const h = input.size?.h ?? PROFILE_H;
  const legs = input.legs;

  /* ---- the x axis: cumulative distance ---- */
  const starts: number[] = [];
  let totalNm = 0;
  for (const l of legs) {
    starts.push(totalNm);
    totalNm += Math.max(0, l.dist_nm || 0);
  }
  const span = Math.max(1e-6, totalNm);
  const x = (nm: number): number => round2((Math.max(0, Math.min(span, nm)) / span) * w);

  /* ---- what has to fit vertically ---- */
  let highestFt: number | null = null;
  const raise = (ft: number | null | undefined): void => {
    if (ft == null || !Number.isFinite(ft)) return;
    if (highestFt === null || ft > highestFt) highestFt = ft;
  };
  for (const l of legs) {
    raise(l.max_terrain_ft);
    raise(l.max_obstacle_ft);
  }
  let ceilingFt = highestFt ?? 0;
  for (let i = 0; i < legs.length; i++) {
    const p = input.planned(i + 1);
    const s = input.suggested(i + 1);
    if (p != null && p > ceilingFt) ceilingFt = p;
    if (s != null && s > ceilingFt) ceilingFt = s;
  }
  const topFt = scaleTopFt(ceilingFt);
  const y = (ft: number): number =>
    round2(PAD_TOP + (1 - Math.max(0, Math.min(topFt, ft)) / topFt) * (h - PAD_TOP));

  /* ---- the terrain envelope ---- */
  const flat: { nm: number; ft: number | null }[] = [];
  legs.forEach((l, i) => {
    const base = starts[i] ?? 0;
    for (const s of (l.samples ?? []) as readonly TerrainSample[]) {
      flat.push({ nm: base + s.nm, ft: s.elev_ft });
    }
  });

  const columns = Math.max(1, Math.min(MAX_COLUMNS, flat.length));
  /**
   * One entry per column: the HIGHEST sample in it and the distance that sample
   * was actually measured at, so a thinned profile still puts its peaks where
   * they are. `null` = a column whose samples all had no elevation (a hole in
   * the archive, which breaks the line); `undefined` = a column no sample fell
   * into, which is bucketing granularity and breaks nothing.
   */
  const bucket: ({ ft: number; nm: number } | null | undefined)[] =
    new Array<{ ft: number; nm: number } | null | undefined>(columns);
  for (const s of flat) {
    const k = Math.min(columns - 1, Math.floor((s.nm / span) * columns));
    const cur = bucket[k];
    if (s.ft == null) {
      // a column keeps a real reading over a hole; a hole only wins an empty column
      if (cur === undefined) bucket[k] = null;
      continue;
    }
    if (cur === undefined || cur === null || s.ft > cur.ft) bucket[k] = { ft: s.ft, nm: s.nm };
  }

  const lines: string[] = [];
  const areas: string[] = [];
  let run: { x: number; y: number }[] = [];
  const flush = (): void => {
    if (run.length === 0) return;
    lines.push(run.map(p => p.x + ',' + p.y).join(' '));
    const first = run[0];
    const last = run[run.length - 1];
    if (first && last) {
      areas.push(
        'M' + first.x + ',' + h + ' L' + run.map(p => p.x + ',' + p.y).join(' L') + ' L' + last.x + ',' + h + ' Z',
      );
    }
    run = [];
  };
  for (let k = 0; k < columns; k++) {
    const col = bucket[k];
    if (col === undefined) continue;
    if (col === null) {
      flush();
      continue;
    }
    run.push({ x: x(col.nm), y: y(col.ft) });
  }
  flush();

  /* ---- per leg: the hazard, the two altitudes and the gap between them ---- */
  const rows: ProfileLeg[] = [];
  const obstacles: ProfileObstacle[] = [];
  let minClearance: number | null = null;
  let worst: ClearanceLevel = 'ok';
  let firstConflict: ProfileModel['firstConflict'] = null;

  legs.forEach((l, i) => {
    const n = i + 1;
    const base = starts[i] ?? 0;
    const top = tallest(l.obstacles ?? []);
    const hazardFt =
      l.max_terrain_ft == null && l.max_obstacle_ft == null
        ? null
        : Math.max(l.max_terrain_ft ?? -Infinity, l.max_obstacle_ft ?? -Infinity);
    const plannedFt = input.planned(n);
    const suggestedFt = input.suggested(n);
    const gap = clearanceFt(plannedFt, hazardFt);
    const level = clearanceLevel(plannedFt, hazardFt, suggestedFt);

    if (gap !== null && (minClearance === null || gap < minClearance)) minClearance = gap;
    if (level === 'none' || (level === 'low' && worst === 'ok')) worst = level;

    const x1 = x(base);
    const x2 = x(base + Math.max(0, l.dist_nm || 0));
    const band =
      plannedFt != null && hazardFt != null
        ? {
            x: x1,
            w: round2(Math.max(0, x2 - x1)),
            y: y(Math.max(plannedFt, hazardFt)),
            h: round2(Math.abs(y(Math.min(plannedFt, hazardFt)) - y(Math.max(plannedFt, hazardFt)))),
          }
        : null;

    for (const o of l.obstacles ?? []) {
      if (o.elev_ft == null) continue;
      const nm = base + o.dist_nm;
      const conflict = plannedFt != null && o.elev_ft >= plannedFt;
      obstacles.push({
        key: n + '-' + o.id,
        id: o.id,
        leg: n,
        nm: round1(nm),
        x: x(nm),
        y: y(o.elev_ft),
        ft: o.elev_ft,
        exact: o.exact,
        name: o.name,
        kind: o.type,
        conflict,
      });
      if (conflict && (firstConflict === null || nm < firstConflict.nm)) {
        firstConflict = { leg: n, nm: round1(nm), x: x(nm), y: y(o.elev_ft) };
      }
    }

    // ground can breach the planned altitude where no obstacle does
    if (
      plannedFt != null &&
      l.max_terrain_ft != null &&
      l.max_terrain_ft >= plannedFt
    ) {
      const nm = base + (l.max_terrain_at_nm ?? 0);
      if (firstConflict === null || nm < firstConflict.nm) {
        firstConflict = { leg: n, nm: round1(nm), x: x(nm), y: y(l.max_terrain_ft) };
      }
    }

    rows.push({
      leg: n,
      startNm: round1(base),
      endNm: round1(base + Math.max(0, l.dist_nm || 0)),
      distNm: round1(Math.max(0, l.dist_nm || 0)),
      x1,
      x2,
      hazardFt: hazardFt === null || !Number.isFinite(hazardFt) ? null : hazardFt,
      maxTerrainFt: l.max_terrain_ft,
      maxTerrainAtNm: l.max_terrain_at_nm == null ? null : round1(base + l.max_terrain_at_nm),
      obstacle:
        top && top.elev_ft != null
          ? { name: top.name, kind: top.type, ft: top.elev_ft, exact: top.exact }
          : null,
      plannedFt,
      suggestedFt,
      clearanceFt: gap,
      level,
      band,
    });
  });

  /* ---- the two step lines ---- */
  const steps = (pick: (r: ProfileLeg) => number | null): string[] => {
    const out: string[] = [];
    let pts: string[] = [];
    for (const r of rows) {
      const ft = pick(r);
      if (ft == null) {
        if (pts.length) out.push(pts.join(' '));
        pts = [];
        continue;
      }
      const yy = y(ft);
      pts.push(r.x1 + ',' + yy, r.x2 + ',' + yy);
    }
    if (pts.length) out.push(pts.join(' '));
    return out;
  };

  const marks = legs.length
    ? [
        ...legs.map((_, i) => ({ index: i, nm: round1(starts[i] ?? 0), x: x(starts[i] ?? 0) })),
        { index: legs.length, nm: round1(totalNm), x: x(totalNm) },
      ]
    : [];

  return {
    w,
    h,
    totalNm: round1(totalNm),
    topFt,
    lines,
    areas,
    obstacles,
    planned: steps(r => r.plannedFt),
    suggested: steps(r => r.suggestedFt),
    legs: rows,
    marks,
    grid: gridSteps(topFt).map(ft => ({ ft, y: y(ft) })),
    highestFt,
    minClearanceFt: minClearance,
    level: worst,
    firstConflict,
    empty: lines.length === 0 && obstacles.length === 0,
  };
}

/* ================================================================== *
 * the two altitude cells
 * ================================================================== */

/**
 * The Sicherheitshöhe cell, read back as feet.
 *
 * The LBA form writes that column in **thousands of feet with one decimal** —
 * `2.5`, `9.0` — which is what `terrain.ts`'s `safeAltCell()` writes into it.
 * `parseAltFt()` must NOT be used here: it reads a bare `3` as three feet and a
 * bare `65` as flight level 65, both of which are right for the Höhe/FL column
 * and wrong for this one. Two columns, two units, two parsers.
 */
export function parseSafeAltFt(raw: string): number | null {
  const v = String(raw ?? '').trim().replace(',', '.');
  if (v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 1000) : null;
}
